import { DateTime as DT } from 'luxon';
import type { ProductSetType } from '@econtractor/types';
import { useAuth } from 'context/AuthContext';
import { useSettings } from 'context/SettingsContext';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import { formatPrice } from 'utils/helpers/functions/formatPrice';
import {
    withBaseCurrency,
    withLocalCurrency,
    withPrimaryCurrency,
} from 'utils/helpers/currency/currencyHelpers';
import CopyTextWrapper from 'components/others/CopyTextWrapper';
import SetTableRowActions from './SetTableRowActions';

interface SetTableRowProps {
    productSet: ProductSetType;
    fetchSets: (force?: boolean) => Promise<void>;
    setEditSetId: (setId: number) => void;
}

const SetTableRow = ({ productSet, fetchSets, setEditSetId }: SetTableRowProps) => {
    const { auth } = useAuth();
    const { localSettings } = useSettings();
    const rate = Number(localSettings?.RATE?.current ?? 1) || 1;
    const isUahMode = rate === 1;
    const price = formatPrice(productSet?.totalPrice);
    const priceSale = formatPrice(productSet?.totalPriceSale);
    const priceUah = formatPrice((productSet?.totalPrice || 0) * rate);
    const priceSaleUah = formatPrice((productSet?.totalPriceSale || 0) * rate);
    const dt = DT.fromMillis(productSet?.setUpdated || 0).toFormat('dd.MM.yyyy');

    return (
        <tr>
            <td className="name">
                <h6
                    className="mb-1 text-break cursor-pointer"
                    onClick={() => setEditSetId(productSet.ID)}
                >
                    {productSet.setName || '-'}
                </h6>
                <p className="text-muted f-12 mb-0 my-1 text-break">{productSet.setDescription || '-'}</p>
            </td>
            <td className="id">
                <code>{productSet.ID}</code>
            </td>
            <td className="article text-truncate">
                <CopyTextWrapper value={productSet.setArticle || ''}>
                    <span title={productSet.setArticle}>{productSet.setArticle}</span>
                </CopyTextWrapper>
            </td>
            <td className="price text-truncate">
                {isUahMode ? (
                    <h6 className="mb-1">{withLocalCurrency(price)}</h6>
                ) : (
                    <h6 className="mb-1" title={withLocalCurrency(priceUah)}>
                        {withBaseCurrency(price)}
                    </h6>
                )}
                <p
                    className="f-12 mb-0 text-primary text-truncate"
                    title={isUahMode ? undefined : withLocalCurrency(priceSaleUah)}
                >
                    {withPrimaryCurrency(priceSale, rate)}
                </p>
            </td>
            <td className="updated">{dt}</td>
            {hasUserAccess(auth?.user, 'prodEdit') && (
                <td className="action">
                    <SetTableRowActions
                        productSet={productSet}
                        fetchSets={fetchSets}
                        setEditSetId={setEditSetId}
                    />
                </td>
            )}
        </tr>
    );
};

export default SetTableRow;
